/**
 * Presentaciones de NIA: normalización del JSON del modelo y exportación a .pptx.
 *
 * Vive aparte de ChatbotLibro por dos razones: el visor y el archivo tienen que
 * mostrar exactamente las mismas diapositivas (de ahí que los dos pasen por
 * `normalizarPresentacion`), y pptxgenjs pesa ~640 KB ya empaquetado, así que se
 * carga con import() dinámico y queda en su propio chunk: sólo lo baja quien
 * descarga una presentación.
 *
 * pptxgenjs se queda en la 3.x a propósito. La 4.x hace `await import('node:fs')`
 * dentro de su código de Node y webpack 5 —el de react-scripts— revienta al leer
 * el esquema "node:" antes de resolverlo ("UnhandledSchemeError: Reading from
 * node:fs is not handled by plugins"). El campo `browser` del paquete no alcanza
 * a mapearlo y CRA no deja tocar la config de webpack sin eject. La 3.x pide los
 * mismos módulos como 'fs'/'https' y ahí sí el campo `browser` los anula.
 */

// Tope duro de diapositivas de contenido (sin contar la portada). El prompt ya lo
// pide (STRUCTURED_EXTRA_RULES en chatService), pero el modelo se pasa de vez en
// cuando y el visor no puede quedar a merced de eso.
export const MAX_DIAPOSITIVAS = 10;

// Paleta de la plataforma (tailwind.config.js): azul Nimbus para títulos y fondo
// de portada, lightBlue para los acentos.
const NIMBUS = '162E5B';
const LIGHT_BLUE = '18BAFB';
const TEXTO = '333333';

const asText = (v) => (v == null ? '' : typeof v === 'string' ? v : String(v));
const asArray = (v) => (Array.isArray(v) ? v : []);

// El modelo a veces manda los puntos ya con viñeta ("- ", "• ", "1. "), y pptxgenjs
// agrega la suya: quedarían dobles.
const limpiarPunto = (punto) =>
  asText(punto).trim().replace(/^([-•*–—]|\d+[.)])\s+/, '').trim();

/**
 * Normaliza la respuesta estructurada a la forma que consumen el visor y el .pptx.
 * Nunca lanza: los campos que falten salen vacíos y las diapositivas sobrantes se
 * recortan a MAX_DIAPOSITIVAS.
 * @param {Object} data - JSON parseado de la Quick Action (structuredType 'presentation')
 * @returns {{ titulo: string, subtitulo: string, diapositivas: Array<{titulo: string, puntos: string[], notas: string}>, advertencias: string[], recortada: boolean }}
 */
export const normalizarPresentacion = (data) => {
  const origen = asArray(data?.diapositivas);
  const diapositivas = origen
    .slice(0, MAX_DIAPOSITIVAS)
    .map((d) => ({
      titulo: asText(d?.titulo).trim(),
      puntos: asArray(d?.puntos).map(limpiarPunto).filter(Boolean),
      notas: asText(d?.notas).trim(),
    }))
    // Una diapositiva sin título ni puntos no aporta nada y en el .pptx sale en blanco.
    .filter((d) => d.titulo || d.puntos.length > 0);

  return {
    titulo: asText(data?.titulo).trim(),
    subtitulo: asText(data?.subtitulo).trim(),
    diapositivas,
    advertencias: asArray(data?.advertencias).map(asText).filter(Boolean),
    recortada: origen.length > MAX_DIAPOSITIVAS,
  };
};

// Nombre de archivo seguro en Windows y macOS.
const nombreArchivo = (titulo) => {
  const base = asText(titulo).trim().replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, ' ').trim();
  return `${(base || 'Presentación').slice(0, 80)}.pptx`;
};

/**
 * Genera y descarga el .pptx de una presentación. Todo ocurre en el navegador:
 * pptxgenjs arma el OOXML y dispara la descarga, no hay llamada al backend.
 * @param {Object} data - JSON estructurado de la presentación (sin normalizar)
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export const exportarPresentacionPptx = async (data) => {
  const { titulo, subtitulo, diapositivas } = normalizarPresentacion(data);

  if (diapositivas.length === 0) {
    return { ok: false, error: 'La presentación no tiene diapositivas para exportar.' };
  }

  try {
    const { default: PptxGenJS } = await import('pptxgenjs');
    const pptx = new PptxGenJS();

    // 16:9 (10 x 5.625 pulgadas) es lo que esperan proyector y Google Slides.
    pptx.layout = 'LAYOUT_16x9';
    pptx.title = titulo || 'Presentación';
    pptx.company = 'Nimbus';

    // Portada
    const portada = pptx.addSlide();
    portada.background = { color: NIMBUS };
    portada.addText(titulo || 'Presentación', {
      x: 0.7, y: 1.9, w: 8.6, h: 1.4,
      fontSize: 36, bold: true, color: 'FFFFFF', valign: 'bottom',
    });
    portada.addShape(pptx.ShapeType.rect, {
      x: 0.75, y: 3.45, w: 1.4, h: 0.07, fill: { color: LIGHT_BLUE },
    });
    if (subtitulo) {
      portada.addText(subtitulo, {
        x: 0.7, y: 3.65, w: 8.6, h: 0.9,
        fontSize: 18, color: 'D8E2F2', valign: 'top',
      });
    }

    // Una diapositiva por entrada: título, acento y puntos como viñetas.
    diapositivas.forEach((d) => {
      const slide = pptx.addSlide();

      slide.addText(d.titulo || '', {
        x: 0.6, y: 0.45, w: 8.8, h: 0.8,
        fontSize: 26, bold: true, color: NIMBUS, valign: 'middle',
      });
      slide.addShape(pptx.ShapeType.rect, {
        x: 0.62, y: 1.28, w: 1.1, h: 0.06, fill: { color: LIGHT_BLUE },
      });

      if (d.puntos.length > 0) {
        slide.addText(
          d.puntos.map((punto) => ({
            text: punto,
            options: { bullet: { code: '2022' }, breakLine: true },
          })),
          {
            x: 0.8, y: 1.65, w: 8.4, h: 3.4,
            fontSize: 16, color: TEXTO, lineSpacingMultiple: 1.35, valign: 'top',
          }
        );
      }

      // Notas del orador: es lo que separa un guión de una lámina suelta.
      if (d.notas) slide.addNotes(d.notas);
    });

    await pptx.writeFile({ fileName: nombreArchivo(titulo) });
    return { ok: true };
  } catch (err) {
    console.error('[Presentación] No se pudo generar el .pptx:', err);
    return { ok: false, error: 'No se pudo generar el archivo. Intenta de nuevo.' };
  }
};
